import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import type { MainStackParamList } from '../types/navigation';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';

type Props = {
  navigation: NativeStackNavigationProp<MainStackParamList, 'SaveSuccess'>;
  route: RouteProp<MainStackParamList, 'SaveSuccess'>;
};

export default function SaveSuccessScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        content: {
          alignItems: 'center',
          maxWidth: 400,
          width: '100%',
        },
        iconContainer: {
          marginBottom: theme.spacing.xl,
        },
        title: {
          ...theme.typography.h2,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.md,
          textAlign: 'center',
        },
        message: {
          ...theme.typography.lg,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: theme.spacing.xxl,
        },
        buttonContainer: {
          width: '100%',
          gap: theme.spacing.md,
        },
      }),
    [theme],
  );

  const { itemCount } = route.params;

  const handleAddAnother = () => {
    navigation.navigate('AddItem');
  };

  const handleViewCache = () => {
    navigation.navigate('Tabs', {
      screen: 'Cache',
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <Ionicons name="checkmark-circle" size={80} color={theme.colors.accent} />
        </View>

        <Text style={styles.title}>Success</Text>

        <Text style={styles.message}>
          {itemCount === 1
            ? 'Your item has been saved to your inventory.'
            : `${itemCount} items have been saved to your inventory.`}
        </Text>

        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={commonStyles.primaryButton}
            onPress={handleViewCache}
          >
            <Text style={commonStyles.primaryButtonText}>View Cache</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={commonStyles.secondaryButton}
            onPress={handleAddAnother}
          >
            <Ionicons name="sparkles-outline" size={20} color={theme.colors.textPrimary} />
            <Text style={commonStyles.secondaryButtonText}>Add Another Item</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}
